<script>
import firebase from 'firebase/app'
import 'firebase/auth'

const createEmptyForm = () => ({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

export default {
  name: 'ChangePasswordDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      form: createEmptyForm(),
      errorMessage: '',
      isSubmitting: false
    }
  },
  computed: {
    isActive: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
        if (!value) this.resetForm()
      }
    },
    canSubmit () {
      return !this.isSubmitting &&
        this.form.currentPassword.length > 0 &&
        this.form.newPassword.length >= 6 &&
        this.form.confirmPassword.length > 0
    }
  },
  methods: {
    resetForm () {
      this.form = createEmptyForm()
      this.errorMessage = ''
      this.isSubmitting = false
    },
    validateForm () {
      if (!this.form.currentPassword || !this.form.newPassword || !this.form.confirmPassword) {
        return 'กรุณากรอกรหัสผ่านให้ครบทุกช่อง'
      }
      if (this.form.newPassword.length < 6) {
        return 'รหัสผ่านใหม่ต้องมีอย่างน้อย 6 ตัวอักษร'
      }
      if (this.form.newPassword.length > 128) {
        return 'รหัสผ่านใหม่ต้องไม่เกิน 128 ตัวอักษร'
      }
      if (this.form.newPassword !== this.form.confirmPassword) {
        return 'รหัสผ่านใหม่และการยืนยันรหัสผ่านไม่ตรงกัน'
      }
      if (this.form.currentPassword === this.form.newPassword) {
        return 'รหัสผ่านใหม่ต้องไม่ซ้ำกับรหัสผ่านปัจจุบัน'
      }
      return ''
    },
    getErrorMessage (error) {
      const messages = {
        'auth/wrong-password': 'รหัสผ่านปัจจุบันไม่ถูกต้อง',
        'auth/invalid-credential': 'รหัสผ่านปัจจุบันไม่ถูกต้อง',
        'auth/weak-password': 'รหัสผ่านใหม่ไม่ปลอดภัย กรุณาใช้รหัสผ่านที่ยาวขึ้น',
        'auth/too-many-requests': 'ลองเปลี่ยนรหัสผ่านหลายครั้งเกินไป กรุณารอสักครู่แล้วลองใหม่',
        'auth/network-request-failed': 'ไม่สามารถเชื่อมต่อระบบได้ กรุณาตรวจสอบอินเทอร์เน็ตแล้วลองใหม่',
        'auth/user-disabled': 'บัญชีนี้ถูกระงับการใช้งาน',
        'auth/user-not-found': 'ไม่พบบัญชีผู้ใช้นี้'
      }

      return messages[error.code] || 'ไม่สามารถเปลี่ยนรหัสผ่านได้ กรุณาลองใหม่อีกครั้ง'
    },
    async submit () {
      if (this.isSubmitting) return

      this.errorMessage = this.validateForm()
      if (this.errorMessage) return

      const user = firebase.auth().currentUser
      if (!user || !user.email) {
        this.errorMessage = 'เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่'
        return
      }

      this.isSubmitting = true

      try {
        const credential = firebase.auth.EmailAuthProvider.credential(
          user.email,
          this.form.currentPassword
        )

        await user.reauthenticateWithCredential(credential)
        await user.updatePassword(this.form.newPassword)
        this.$emit('password-changed')
      } catch (error) {
        this.errorMessage = this.getErrorMessage(error)
        this.isSubmitting = false
      }
    }
  }
}
</script>

<template>
  <vs-popup
    class-content="change-password-dialog"
    title="เปลี่ยนรหัสผ่าน"
    :active.sync="isActive">
    <form class="change-password-form" @submit.prevent="submit">
      <p class="change-password-help">
        หลังเปลี่ยนรหัสผ่านสำเร็จ ระบบจะออกจากระบบเพื่อให้เข้าสู่ระบบด้วยรหัสใหม่
      </p>

      <vs-input
        v-model="form.currentPassword"
        name="current-password"
        type="password"
        label="รหัสผ่านปัจจุบัน"
        autocomplete="current-password"
        class="change-password-input w-full" />

      <vs-input
        v-model="form.newPassword"
        name="new-password"
        type="password"
        label="รหัสผ่านใหม่"
        autocomplete="new-password"
        class="change-password-input w-full" />

      <vs-input
        v-model="form.confirmPassword"
        name="confirm-password"
        type="password"
        label="ยืนยันรหัสผ่านใหม่"
        autocomplete="new-password"
        class="change-password-input w-full" />

      <p v-if="errorMessage" class="change-password-error" role="alert">
        {{ errorMessage }}
      </p>

      <div class="change-password-actions">
        <vs-button
          type="border"
          color="primary"
          :disabled="isSubmitting"
          @click.prevent="isActive = false">
          ยกเลิก
        </vs-button>
        <vs-button
          button="submit"
          color="primary"
          :disabled="!canSubmit">
          {{ isSubmitting ? 'กำลังเปลี่ยนรหัสผ่าน...' : 'เปลี่ยนรหัสผ่าน' }}
        </vs-button>
      </div>
    </form>
  </vs-popup>
</template>

<style scoped>
.change-password-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.change-password-help {
  margin: 0 0 0.25rem;
  color: #626262;
  line-height: 1.6;
}

.change-password-input {
  margin-top: 0.25rem;
}

.change-password-error {
  margin: 0;
  color: #ea5455;
  font-size: 0.875rem;
}

.change-password-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
</style>
